/**
 * Project: Gus's Auto Dealership
 * Author: Eihab Khudhair
 * Description: Financing page with professional UI, animated elements, and seamless navigation.
 * Version: 2.1 (Desktop-safe call UX + blue homepage button)
 */

'use client';

import Link from 'next/link';
import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { ArrowRight, Shield, TrendingUp, Clock, Phone, Copy, Check } from 'lucide-react';

export default function FinancingPage() {
  const phoneNumberDisplay = '(614) 772-0734';
  const phoneNumberTel = '+16147720734';

  const isMobile = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return /Android|iPhone|iPad|iPod|Opera Mini|IEMobile/i.test(navigator.userAgent);
  }, []);

  const [showCallMsg, setShowCallMsg] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCallClick = () => {
    if (isMobile) {
      window.location.href = `tel:${phoneNumberTel}`;
      return;
    }
    // Desktop: show message inside the card instead of triggering OS popup
    setShowCallMsg(true);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(phoneNumberDisplay);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <main className="container mx-auto px-4 py-12 md:py-20">
        <div className="max-w-6xl mx-auto">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full mb-6 shadow-lg"
            >
              <TrendingUp className="w-10 h-10 text-white" />
            </motion.div>

            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
              Financing Solutions
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We&apos;re establishing premium partnerships with leading financial institutions to deliver
              exceptional value to our valued customers.
            </p>
          </motion.div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* Left Column */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white rounded-2xl shadow-xl p-8 md:p-10 border border-gray-100"
            >
              <div className="flex items-start mb-6">
                <div className="bg-blue-100 p-3 rounded-lg mr-4">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">Coming Soon</h2>
                  <p className="text-gray-600">
                    We&apos;re working diligently to bring you the most competitive financing options in the market.
                    Our team is negotiating exclusive rates and terms with top-tier financial partners.
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                {[
                  'Competitive interest rates tailored to your needs',
                  'Flexible payment terms with minimal documentation',
                  'Quick approval process for qualified buyers',
                  'Special offers for first-time buyers and loyal customers',
                ].map((text) => (
                  <div key={text} className="flex items-start">
                    <Shield className="w-5 h-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">{text}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right Column */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="space-y-8"
            >
              {/* Call Now Card */}
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl shadow-lg p-8 border border-blue-100">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Get Notified First</h3>
                <p className="text-gray-600 mb-6">
                  Call our sales team directly to learn about current and upcoming financing options.
                </p>

                <button
                  type="button"
                  onClick={handleCallClick}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold py-3 px-6 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 flex items-center justify-center shadow-md hover:shadow-lg transform hover:-translate-y-1"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call {phoneNumberDisplay}
                </button>

                {/* Desktop-only "inside the field" message */}
                {showCallMsg && !isMobile && (
                  <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-4">
                    <p className="text-sm text-gray-700">
                      You&apos;re on a computer. Please call us at{' '}
                      <span className="font-bold text-blue-700">{phoneNumberDisplay}</span> from your phone.
                    </p>

                    <div className="mt-3 flex flex-col sm:flex-row gap-3">
                      <button
                        type="button"
                        onClick={handleCopy}
                        className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-white border border-blue-200 text-blue-700 font-semibold hover:bg-blue-100 transition"
                      >
                        {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                        {copied ? 'Copied' : 'Copy Number'}
                      </button>

                      <button
                        type="button"
                        onClick={() => setShowCallMsg(false)}
                        className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-transparent text-gray-600 font-semibold hover:text-gray-900 transition"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Navigation */}
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Explore</h3>
                <div className="space-y-4">
                  {[
                    { href: '/inventory', label: 'Browse Inventory' },
                    { href: '/contact', label: 'Contact Us' },
                  ].map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      className="flex items-center justify-between p-4 bg-gray-50 hover:bg-blue-50 rounded-xl transition-all duration-300 group"
                    >
                      <span className="font-medium text-gray-700 group-hover:text-blue-600">{item.label}</span>
                      <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-2 transition-transform" />
                    </Link>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Bottom CTA */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-center"
          >
            <Link
              href="/"
              className="inline-flex items-center justify-center bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold py-3 px-10 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Return to Homepage
            </Link>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
